import * as React from "react";
import { useNavigate } from "@opendash/router";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  InfoBox,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import styled from "styled-components";

// Custom Styled Components (spezifisch für diese Route)
const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "FAZIT";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

const CalloutBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%);
  border-left: 4px solid #5c6bc0;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "HINWEIS";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #5c6bc0;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #283593;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

const ChartContainer = styled.div`
  margin: 30px 0;

  p {
    font-size: 0.9em;
    color: #666;
    text-align: center;
    margin-top: 10px;
    font-style: italic;
  }
`;

/**
 * WasserknappheitEntstehungRoute – Wie und wo entsteht Wasserknappheit
 */
export const WasserknappheitEntstehungRoute = React.memo(
  function WasserknappheitEntstehungRoute() {
    const navigate = useNavigate();
    const { t } = useTexts();

    // Chart 1: Durchschnittliche Süßwassererneuerungstiefe
    const renewalDepthChartOptions: Highcharts.Options = {
      chart: {
        type: "bar",
        backgroundColor: "transparent",
        height: 400,
      },
      title: {
        text: "Durchschnittliche Erneuerungsrate von Süßwasser (mm/Jahr)",
        style: {
          fontSize: "14px",
          fontWeight: "normal",
        },
      },
      xAxis: {
        categories: [
          "Nordamerika",
          "Südamerika",
          "Europa",
          "Afrika",
          "Asien",
          "Australien & Ozeanien",
        ],
        title: {
          text: null,
        },
      },
      yAxis: {
        min: 0,
        max: 800,
        title: {
          text: null,
        },
        labels: {
          format: "{value}",
        },
        gridLineWidth: 1,
        tickInterval: 200,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            format: "{point.y}",
          },
          color: "#2196f3",
        },
      },
      series: [
        {
          type: "bar",
          name: "Erneuerungsrate (mm/Jahr)",
          data: [281, 715, 626, 131, 273, 101],
        },
      ],
      credits: {
        enabled: false,
      },
    };

    // Chart 2: Pro-Kopf-Süßwassererneuerungsrate (Platzhalter - warte auf Daten)
    const perCapitaChartOptions: Highcharts.Options = {
      chart: {
        type: "bar",
        backgroundColor: "transparent",
        height: 400,
      },
      title: {
        text: "Frischwassererneuerung pro Kopf",
        style: {
          fontSize: "14px",
          fontWeight: "normal",
        },
      },
      xAxis: {
        categories: [
          "Nordamerika",
          "Südamerika",
          "Europa",
          "Afrika",
          "Asien",
          "Australien & Ozeanien",
        ],
        title: {
          text: null,
        },
      },
      yAxis: {
        min: 0,
        max: 35000,
        title: {
          text: null,
        },
        labels: {
          format: "{value}",
        },
        gridLineWidth: 1,
        tickInterval: 10000,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            format: "{point.y:,.0f}",
          },
          color: "#2196f3",
        },
      },
      series: [
        {
          type: "bar",
          name: "Pro Kopf (m³/Jahr)",
          data: [11904, 30428, 8894, 3318, 2697, 28225],
        },
      ],
      credits: {
        enabled: false,
      },
    };

    const tabs = [
      {
        title: t("wasserknappheit.tab.grundlagen", "Grundlagen"),
        path: "/wasserklaerer/wasserknappheit/grundlagen",
        isActive: false,
      },
      {
        title: t("wasserknappheit.tab.entstehung", "Entstehung"),
        path: "/wasserklaerer/wasserknappheit/entstehung",
        isActive: true,
      },
      {
        title: t("wasserknappheit.tab.fussabdruck", "Wasserfußabdruck"),
        path: "/wasserklaerer/wasserknappheit/fussabdruck",
        isActive: false,
      },
    ];

    return (
      <AppLayout>
        <ContentContainer>
          <h1>{t("wasserknappheit.title", "Wasserknappheit")}</h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          {/* Einleitung */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.entstehung.title",
                "Wie und wo entsteht Wasserknappheit"
              )}
            </h2>
            <p>
              {t(
                "wasserknappheit.entstehung.intro1",
                "Wasserknappheit lässt sich keiner einzelnen Ursache eindeutig zuschreiben. Weder Landwirtschaft, Industrie noch Klimawandel verursachen sie per se. Wasser ist Teil eines komplexen Kreislaufs, in dem sich Faktoren je nach Region unterschiedlich auswirken. Entsprechend müssen auch die Lösungsansätze regional differenziert betrachtet werden. Die natürlichen Ausgangsbedingungen, die Verfügbarkeit von Wasserressourcen und der technologische Entwicklungsstand variieren von Land zu Land erheblich. In manchen Regionen kann die Industrie beträchtliche Wassermengen nutzen, ohne die Erneuerungsrate der Ressourcen zu gefährden – etwa, weil überwiegend Oberflächenwasser entnommen, aufbereitet und wieder in den Kreislauf eingespeist wird. In anderen Gebieten führen vergleichbare Entnahmemengen aufgrund begrenzter Wasserreserven oder fehlender Aufbereitungsmöglichkeiten rasch zu Versorgungsengpässen."
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.entstehung.intro2",
                "Dieses Kapitel verfolgt das Ziel, die Zusammenhänge sichtbar zu machen: Wohin fließt das Wasser? Wofür wird es genutzt – und vor allem, wie intensiv wird es in den jeweiligen Sektoren genutzt?"
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.entstehung.intro3",
                "Wichtig ist: Es werden keine pauschalen Urteile über bestimmte Regionen getroffen. Jede Region sollte ihre Situation auf Grundlage lokaler Daten bewerten. Zugleich lassen sich allgemeine Hauptfaktoren erkennen, die Wasserknappheit häufig verursachen und in Zukunft weiter verschärfen können: übermäßige Entnahmen gegenüber der natürlichen Neubildung, Qualitätsverluste durch Verschmutzung, Defizite in Infrastruktur und Wasserressourcenmanagement sowie klimatische Extremereignisse. Wer diese Treiber versteht, kann Risiken früh erkennen und wirksame, kontextspezifische Maßnahmen ableiten."
              )}
            </p>
          </Card>

          {/* Eine unfaire Grundverteilung */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.entstehung.distribution.title",
                "Eine unfaire Grundverteilung"
              )}
            </h2>
            <p>
              {t(
                "wasserknappheit.entstehung.distribution.text1",
                "Wasser ist auf der Erde ungleich verteilt. Bereits natürliche Gegebenheiten führen zu einer unausgewogenen Ausgangssituation, an die sich Menschen, Tiere und Ökosysteme anpassen müssen."
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.distribution.text2",
                  "Aus den absoluten Wassermengen der Kontinente (Kapitel F1) lässt sich keine direkte Aussage über die Trockenheit oder Wasserverfügbarkeit einzelner Regionen ableiten. Für eine aussagekräftigere Beurteilung ist die sogenannte Erneuerungsrate des Wassers relevant. Sie beschreibt die Menge an Wasser, die durch den natürlichen Wasserkreislauf (Niederschläge, Fließgewässer, Grundwasserbewegungen, Verdunstung) jährlich neu bereitgestellt wird. Diese Rate stellt die theoretische Obergrenze einer nachhaltigen Wassernutzung dar. [1, S.12]"
                )
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.entstehung.distribution.text3",
                "Um regionale Unterschiede sinnvoll zu bewerten, sollte die Erneuerungsrate sowohl auf die Fläche eines Kontinents als auch auf die Bevölkerungszahl bezogen werden. Dadurch wird ein Vergleich der tatsächlichen Wasserverfügbarkeit ermöglicht."
              )}
            </p>

            <ChartContainer>
              <HighchartsReact
                highcharts={Highcharts}
                options={renewalDepthChartOptions}
              />
              <FigureCaption>
                Abbildung 1:{" "}
                {t(
                  "wasserknappheit.entstehung.chart1.caption",
                  "Durchschnittliche Süßwassererneuerungstiefe [eigene Darstellung nach UNESCO, 2022, S.13 [1]]"
                )}
              </FigureCaption>
            </ChartContainer>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.distribution.text4",
                  "Die durchschnittliche Süßwassererneuerungstiefe – also die Menge an erneuerbarem Wasser, die jährlich pro Flächeneinheit zur Verfügung steht – ist in den feuchteren Kontinenten Südamerika und Europa vier- bis siebenmal höher als in Asien, Afrika sowie Australien und Ozeanien. [1, S.14]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.distribution.text5",
                  "Dieser Indikator ermöglicht Rückschlüsse auf die klimatischen Verhältnisse, den Trockenheitsgrad einer Region und die ökologischen Eigenschaften der Landschaft. Die genannten Kontinente mit geringeren Erneuerungsraten zeichnen sich häufig durch ausgedehnte aride (trockene) und semiaride (halbtrockene) Gebiete aus, in denen Niederschläge selten und unregelmäßig auftreten. [1, S.13]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.distribution.text6",
                  "Die Pro-Kopf-Süßwassererneuerungsrate ergänzt diese Betrachtung um eine bevölkerungsbezogene Perspektive. Sie beschreibt, wie viel erneuerbares Süßwasser theoretisch jedem Menschen zur Verfügung steht und damit, wie groß das Potenzial für eine nachhaltige Wassernutzung ist. In diesem Zusammenhang weisen Afrika und Asien die niedrigsten Pro-Kopf-Erneuerungsraten auf. [1, S.14]"
                )
              )}
            </p>

            <ChartContainer>
              <HighchartsReact
                highcharts={Highcharts}
                options={perCapitaChartOptions}
              />
              <FigureCaption>
                Abbildung 2:{" "}
                {t(
                  "wasserknappheit.entstehung.chart2.caption",
                  "Frischwassererneuerung pro Kopf [eigene Darstellung nach UNESCO, 2022, S.14 [1]]"
                )}
              </FigureCaption>
            </ChartContainer>
          </Card>

          {/* Landwirtschaft */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.entstehung.agriculture.title",
                "Landwirtschaft"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.agriculture.text1",
                  "Eine der Hauptursachen für die Erschöpfung von Grundwasserleitern ist die landwirtschaftliche Grundwasserentnahme. Der Zugang zu Wasserressourcen wurde in den vergangenen Jahrzehnten durch kostengünstigere Pumpen-, Bohr- und Energietechnologien deutlich erleichtert, oftmals unterstützt durch staatliche Förder- und Subventionsprogramme. [1, S.52] Das weltweite Bevölkerungswachstum und der steigende Wohlstand führen zu einer kontinuierlich wachsenden Nachfrage nach Nahrungsmitteln. Diese Entwicklungen führen zu einer Ausweitung der landwirtschaftlichen Produktion und damit zu einem wachsenden Wasserbedarf. [1, S.52]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.agriculture.text2",
                  "Dabei zeigt sich eine kritische Wechselwirkung: Bereits hohe Entnahmeraten, auch ohne zusätzliche Schadstoffeinträge, können durch physikalische Veränderungen des Untergrunds Grundwasserverschmutzungen verursachen. Eine übermäßige Entnahme führt zur Verdichtung komprimierbarer Erdschichten. Dies kann Bodensenkungen, das Nachsickern von minderwertigem Grundwasser sowie Salzintrusionen in Grundwasserleiter und Küstengewässer zur Folge haben. Da es sich bei diesem Aspekt um die übermäßige Grundwassernutzung im Allgemeinen handelt, gelten die genannten Folgen im weiteren Verlauf des Kapitels nicht nur für die Landwirtschaft, sondern ebenso für die industrielle Nutzung und die öffentliche Wasserversorgung im Zuge der Urbanisierung. [1, S. 35, 52]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.agriculture.text3",
                  "Neben der quantitativen Erschöpfung und der daraus resultierenden qualitativen Beeinträchtigung der Grundwasserkörper verursacht die Landwirtschaft zusätzlich aktive Verunreinigungen des Grundwassers. Sie gilt heute als weltweit führende Quelle der Wasserverschmutzung, noch vor Siedlungs- und Industrieabwässern. [1, S.54]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.agriculture.text4",
                  "Zu den typischen landwirtschaftlichen Schadstoffen gehören Nährstoffe, Pestizide, Salze, Sedimente, organischer Kohlenstoff, Krankheitserreger, Metalle und Arzneimittelrückstände. Besonders problematisch ist Nitrat aus mineralischen und organischen Düngemitteln. Es stellt den am weitesten verbreiteten anthropogenen Schadstoff im Grundwasser dar und verursacht in Oberflächengewässern häufig Eutrophierung. [1, S.35, 54]"
                )
              )}
            </p>

            <InfoBox>
              <h4>
                {t(
                  "wasserknappheit.entstehung.agriculture.eutrophierung.title",
                  "Eutrophierung"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "wasserknappheit.entstehung.agriculture.eutrophierung.text",
                    "Eutrophierung bezeichnet den Prozess, bei dem menschliche Aktivitäten eine übermäßige Anreicherung von Nährstoffen in ursprünglich nährstoffarmen Gewässern verursachen. Dadurch kommt es zu einem übermäßigen Wachstum von Algen und Wasserpflanzen, das anderen Pflanzenarten, Kleinstlebewesen und Tieren die Lebensgrundlage entzieht. [2]"
                  )
                )}
              </p>
            </InfoBox>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.agriculture.text5",
                  "In der Europäischen Union stehen rund 38 % der Gewässer unter erheblichem Druck durch landwirtschaftliche Belastungen. [1, S.54]"
                )
              )}
            </p>
          </Card>

          {/* Bevölkerungswachstum und Urbanisierung */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.entstehung.urbanization.title",
                "Bevölkerungswachstum und Urbanisierung"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.urbanization.text1",
                  "Wasserknappheit entsteht in erheblichem Maße auch infolge des anhaltenden Bevölkerungswachstums. Mit jedem weiteren Menschen steigt der Bedarf an sauberem Trinkwasser, während die verfügbaren Wasserressourcen begrenzt bleiben. [1, S.66]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.urbanization.text2",
                  "Die Urbanisierung verstärkt dieses Problem zusätzlich. Wenn sich Menschen in Städten und Ballungszentren konzentrieren, steigt der Wasserverbrauch auf engem Raum stark an. Eine Region hat aber eine absolute Ressourcengrenze. [1, S.69] Sobald die Nachfrage die natürliche Erneuerungsrate des Grundwassers übersteigt, drohen Übernutzung und ein Absinken des Grundwasserspiegels. Auch in diesem Fall führt der quantitative Rückgang zu einer qualitativen Verschlechterung des Grundwassers. (vgl. Abschnitt Landwirtschaft) In dicht besiedelten und ärmeren Gebieten kommt es zusätzlich zu Verschmutzungen durch Abwässer und Industrieabfälle, insbesondere dort, wo die Infrastruktur mit dem Wachstum der Städte nicht Schritt hält. Dort kann das schnelle Wachstum die bestehenden Wasser- und Abwassersysteme überfordern, was zu ungeregelter Nutzung und zunehmender Verunreinigung führt. [1, S.69]"
                )
              )}
            </p>

            <InfoBox>
              <h4>
                {t(
                  "wasserknappheit.entstehung.urbanization.example.title",
                  "Beispiel: Lusaka (Sambia)"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "wasserknappheit.entstehung.urbanization.example.text",
                    "Die Stadt Lusaka (Sambia) ist stark vom lokalen Grundwasser abhängig. Ihre Bevölkerung stieg von etwa 500.000 Einwohnern im Jahr 1978 auf 2,8 Millionen im Jahr 2018. Der kommunale Wasserversorger betreibt 228 Brunnen, die rund 140 Millionen Liter Wasser pro Tag liefern. Eine zusätzliche Flussaufbereitungsanlage liefert weitere 80 Millionen Liter täglich. Neben dem öffentlichen System existieren tausende private Brunnen, die zusammen bis zu 300 Millionen Liter pro Tag fördern. In ärmeren Stadtteilen greifen viele Haushalte auf flache, handgegrabene Brunnen zurück, da das Grundwasser dort nur wenige Meter unter der Oberfläche liegt. Diese Brunnen liegen jedoch in dolomitischem Kalkstein, der besonders anfällig für Verschmutzung durch Abwasser und Industrieabfälle ist. Die weit verbreiteten Grubenlatrinen stellen eine zusätzliche Belastung dar, da sie das Grundwasser direkt kontaminieren. Dadurch kommt es regelmäßig zu Cholera-Ausbrüchen. Zwar gibt es Großprojekte zum Ausbau der Kanalisation und Abwasserbehandlung, doch in informellen Siedlungen sind diese Vorhaben technisch schwierig und teuer umzusetzen. [1, S.69]"
                  )
                )}
              </p>
            </InfoBox>

            <CalloutBox>
              <p>
                {t(
                  "wasserknappheit.entstehung.urbanization.fazit.text",
                  "Das Beispiel Lusaka verdeutlicht, wie in schnell wachsenden Städten die Kombination aus Bevölkerungsdruck, unzureichender Infrastruktur und Grundwasserabhängigkeit zu Wasserknappheit und erheblichen Gesundheitsrisiken führt."
                )}
              </p>
            </CalloutBox>
          </Card>

          {/* Industrie */}
          <Card>
            <h2>
              {t("wasserknappheit.entstehung.industry.title", "Industrie")}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.industry.text1",
                  "Der Einfluss industrieller Tätigkeiten auf die Grundwasservorkommen variiert regional erheblich, da die Nutzungsprofile des Grundwassers für die Industrie sehr unterschiedlich sind. Beispiele verdeutlichen die Spannweite: In Japan entfallen Schätzungen zufolge rund 48 % der Grundwasserentnahmen auf die Industrie. In den USA liegt dieser Anteil bei etwa 6 %. [1, S.75] Solche Unterschiede bedeuten jedoch nicht automatisch höhere oder geringere Nachhaltigkeit. Maßgeblich sind die lokale Neubildungsrate des Grundwassers, die betriebliche Effizienz, das Qualitätsmanagement zur Vermeidung von Verunreinigung sowie die Nutzung alternativer Quellen wie Oberflächen- oder aufbereitetes Prozesswasser. [1, S.79, S.88]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.industry.subtitle",
                "Wofür wird Wasser in der Industrie gebraucht?"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.industry.text2",
                  "Grundwasser wird in erster Linie dort bevorzugt, wo eine hohe Wasserqualität erforderlich ist, da es im Vergleich zu Oberflächenwasser geringere Verunreinigungen aufweist und daher weniger Aufbereitung erfordert. [1, S.76] Die industrielle Wassernutzung lässt sich in mehrere Hauptanwendungsfelder gliedern:"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.industry.process.title",
                "1) Prozesswasser"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.industry.process.text",
                  "Wasser dient als zentrales Betriebsmittel in Herstellungs- und Veredelungsprozessen, etwa in der Chemie- und Lebensmittelproduktion. Besonders wasserintensiv sind die Textil- und Bekleidungsindustrie, die Lederindustrie sowie die Zellstoff- und Papierindustrie. In der Nassveredelung von Baumwolle werden beispielsweise 250–350 Liter Wasser pro Kilogramm Stoff benötigt. In der Textilproduktion werden nahezu alle Farbstoffe, Spezial- und Veredelungschemikalien in Wasserbädern appliziert; auch die Stoffvorbereitung (Entschlichten, Waschen, Bleichen, Mercerisieren) erfolgt überwiegend in wässrigen Systemen, häufig unter Verwendung von Grundwasser. Die Kehrseite: Unbehandeltes oder unzureichend gereinigtes Industrieabwasser kann etwa über Injektionsbrunnen ins Grundwasser gelangen und es verschmutzen. Das gefährdet nachgelagerte Nutzungen wie Bewässerung, Trinkwassergewinnung und industrielle Prozesse. In Tiruppur (Indien) wurden beispielsweise erhöhte Salzgehalte im Grundwasser nachgewiesen, die sich auf die intensive Textilverarbeitung zurückführen lassen. [1, S.76, 77]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.industry.cleaning.title",
                "2) Waschen und Reinigen"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.industry.cleaning.text",
                  "Viele Produktionsketten benötigen in der Endphase Waschwasser, um Rückstände von Prozesschemikalien zu entfernen. Diese Stoffe gelangen ins Abwasser und müssen vor einer Einleitung oder Versickerung entsprechend behandelt werden. Ohne adäquate Behandlung entstehen Risiken für Umwelt und Gesundheit. [1, S.77]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.industry.cooling.title",
                "3) Kühlwasser"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.industry.cooling.text",
                  "Energie- und Grundstoffindustrien zählen zu den größten Nutzern von Kühlwasser. Energieintensive Anlagen benötigen große Durchflussmengen, um Prozesse auf Betriebstemperatur zu halten. Als Richtwerte gelten etwa 30 m³ Wasser pro Tonne Stahl in Hüttenwerken und rund 1,5 m³ pro Kubikmeter Rohöl in Raffinerien. [1, S.77] Auch wenn der Energiesektor vielerorts wenig Grundwasser direkt nutzt, kann er die Grundwasserqualität erheblich beeinflussen. Ein Beispiel hierfür sind Kohleaschedeponien, die über Jahrzehnte Schadstoffe ins Grundwasser auslaugen können. Ebenso birgt Fracking – insbesondere in flachen Aquiferen – Verschmutzungsgefahr durch Formationswasser sowie Bohr- und Fracking-Flüssigkeiten, wenn Schutz- und Behandlungsstandards fehlen oder versagen. [1, S.85]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.industry.mining.title",
                "4) Bergbau und unterirdische Bauwerke"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.industry.mining.text",
                  "Wasser ist im Bergbau doppelt relevant. Nutzenseite: Es wird für Gewinnung, Zerkleinerung, Trennung, Erzaufbereitung, Staubbindung, Schlammtransport und Waschen benötigt. Lastenseite: Grundwasser behindert den Betrieb, weil Gruben entwässert werden müssen. Solche Entwässerungsmaßnahmen können den Grundwasserspiegel deutlich absenken und dadurch lokale Wasserversorgungen beeinträchtigen. Zudem besteht das Risiko einer Verunreinigung der Aquifere. Ist das abgepumpte Wasser belastet, sind aufwendige Behandlungen und eine gesicherte Entsorgung erforderlich, was die Betriebskosten erhöht. [1, S.80] Darüber hinaus können Schadstoffe aus stillgelegten Bergwerken, belasteten Böden oder alten Deponien ausgewaschen werden und Umwelt sowie Gesundheit beeinträchtigen."
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.industry.mining.text2",
                  "Auch Partikel aus Industrieabgasen, die auf den Boden fallen, können durch Regen in den Untergrund gespült werden und das Grundwasser erreichen. [1, S.78]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.industry.solutions.title",
                "Lösungsansätze"
              )}
            </h3>

            <CalloutBox>
              <h4>
                {t(
                  "wasserknappheit.entstehung.industry.eip.title",
                  "Eco-Industrial Parks (EIPs)"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "wasserknappheit.entstehung.industry.eip.text",
                    "Die Vereinten Nationen haben mit dem Konzept der Eco-Industrial Parks (EIPs) auf das Problem reagiert und entsprechende Nachhaltigkeitsziele formuliert. Diese sogenannten 'Öko-Industrieparks' gewinnen weltweit zunehmend an Bedeutung. EIPs sollen nachhaltiges Wassermanagement, Nutzung, Effizienz und Behandlung priorisieren. EIPs setzen Wasser verantwortungsvoll ein, berücksichtigen lokale Wasserknappheit und empfindliche Reservoire. Abwasser muss aufbereitet und die Schließung betrieblicher Wasserkreisläufe gefördert werden. [1, S.79]"
                  )
                )}
              </p>
            </CalloutBox>

            <CalloutBox>
              <h4>
                {t(
                  "wasserknappheit.entstehung.industry.zld.title",
                  "ZLD-Systeme - Zero Liquid Discharge"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "wasserknappheit.entstehung.industry.zld.text",
                    "ZLD heißt - kein flüssiges Abwasser verlässt die Fabrik. Das Abwasser wird derart aufbereitet, dass es erneut im Produktionsprozess eingesetzt werden kann. Die Aufbereitung erfolgt in mehreren Stufen, häufig unter Einsatz von Membrantechnologien sowie biologischen oder chemischen Verfahren. Die indische Regierung hat ZLD ab 2006 in Tamil Nadu per Gesetz für die Textil- und Bekleidungsindustrie vorgeschrieben. Viele Fabriken wurden vom Obersten Gericht des Bundesstaats geschlossen, weil sie die Vorgaben nicht einhielten. Da die vollständige Rückgewinnung des Abwassers mit überproportional hohen Kosten verbunden ist, setzen viele Betriebe auf das sogenannte Minimal Liquid Discharge (MLD)-Verfahren, bei dem nahezu das gesamte Wasser zurückgewonnen wird – wenn auch nicht vollständig. [1, S.80]"
                  )
                )}
              </p>
            </CalloutBox>
          </Card>

          {/* Klimawandel */}
          <Card>
            <h2>
              {t("wasserknappheit.entstehung.climate.title", "Klimawandel")}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.intro",
                  "Grundlegend kann sich der Klimawandel in zweierlei Hinsicht auf unser Grundwasservorkommen auswirken: direkt durch Veränderungen der Wasserbilanz an der Erdoberfläche und indirekt durch veränderte Entnahmemuster des Menschen, wenn Gesellschaften auf eine verringerte Süßwasserverfügbarkeit reagieren müssen. [1, S.102]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.climate.direct.title",
                "Direkte Auswirkungen des Klimawandels auf regionale Wasserbilanzen"
              )}
            </h3>
            <p>
              {t(
                "wasserknappheit.entstehung.climate.direct.text1",
                "Der Klimawandel verändert die Wasserbilanz, indem er sowohl den Zeitpunkt und die Menge der Grundwasserneubildung als auch die Qualität des neugebildeten Wassers beeinflusst."
              )}
            </p>

            <h4>
              {t(
                "wasserknappheit.entstehung.climate.precipitation.title",
                "Auswirkungen auf Niederschläge und Extremereignisse"
              )}
            </h4>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.precipitation.text1",
                  "Prognosen zum Einfluss des Klimawandels auf die Grundwasserneubildung sind mit erheblichen Unsicherheiten behaftet, da globale Modellierungen die komplexen Prozesse nur begrenzt abbilden können. Hauptursache sind die begrenzten Möglichkeiten bestehender Modelle, die komplexen hydrologischen Prozesse adäquat zu erfassen. Einige grundlegende Veränderungen lassen sich jedoch bereits absehen. [1, S.102]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.precipitation.text2",
                  "Was passiert, wenn die Temperatur auf der Erde ansteigt? Die Verdunstungsraten nehmen zu, da wärmere Luft mehr Wasserdampf aufnehmen kann. [1, S.104] In absoluten Mengen verschiebt sich ein Teil des Wassers von der flüssigen in die gasförmige Phase – eine Form, die für die menschliche Nutzung deutlich schwerer zugänglich ist. Zwischen 2003 und 2019 stieg die globale Evapotranspiration um etwa zehn Prozent an. [1, S.102]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.precipitation.text3",
                  "Da bei wärmerer Luft höhere Taupunkte erreicht werden müssen, ist mehr Evapotranspiration erforderlich, bis Kondensation und Niederschlag einsetzen. Das führt generell zu einem Rückgang leichter Regenfälle und zu einer Zunahme intensiver Starkniederschläge. Zu den Folgen zählen stärkere Schwankungen der Bodenfeuchte, häufigere Überschwemmungen sowie länger andauernde und intensivere Dürren. [1, S.104] Starkregen kann trotz Flutrisiko aber das Grundwasser besser auffüllen, vor allem in Tropen und Trockengebieten. Dort bilden sich nach intensiven Niederschlägen Pfützen/Teiche, aus denen Wasser gezielt in den Boden sickert. Dieser Effekt wurde auch in Australien und im Südwesten der USA beobachtet. [1, S.104]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.precipitation.text4",
                  "Auch dieser Effekt lässt sich jedoch nicht pauschal auf alle Regionen übertragen, da ökologische und klimatische Wechselwirkungen komplex sind. Feuchtere Bedingungen führen nicht zwangsläufig zu einer stärkeren Grundwasserneubildung. Während der ENSO-Jahre kam es in der US-Südwestregion bei bis zu 2,5-fach höheren Winterniederschlägen zu verstärktem Pflanzenwachstum ('Wüstenblüten'), wodurch der zusätzliche Niederschlag durch erhöhte Evapotranspiration weitgehend wieder verloren ging. [1, S.103]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.precipitation.text5",
                  "Starkregen hat aber auch eine Kehrseite: Er kann die Qualität des Grundwassers beeinträchtigen. Denn starke Regenfälle können einfacher Schadstoffe und Krankheitserreger aus schlecht eingedämmten Abfällen, gelagerten Chemikalien oder Fäkalien mobilisieren und in den Boden zum Grundwasserleiter spülen. Besonders gefährdet sind Gebiete mit unzureichender sanitärer Versorgung. Es gibt Fälle, in denen starke Regenfälle bereits mit Durchfallerkrankungen, einschließlich Cholera, in Verbindung gebracht wurden. [1, S.107]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.precipitation.text6",
                  "Hinsichtlich der zukünftigen Niederschlagsverteilung besteht weiterhin Unsicherheit darüber, wo, wann und in welchem Ausmaß Regen oder Schnee fallen werden. Eine übliche Formel lautet 'nass wird nasser, trocken wird trockener'. Diese sei jedoch zu simpel, denn für feuchte äquatoriale Landregionen in Amerika und Asien werden teils deutliche Niederschlagsrückgänge projiziert. Prognosen deuten darauf hin, dass die größten Niederschlagszunahmen künftig über tropischen Ozeanen und nicht über Landflächen auftreten werden – also in Regionen, in denen sie für die Süßwasserversorgung kaum nutzbar sind. [1, S.102, 103]"
                )
              )}
            </p>

            <h4>
              {t(
                "wasserknappheit.entstehung.climate.sealevel.title",
                "Meeresspiegelanstieg und Versalzung"
              )}
            </h4>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.sealevel.text",
                  "Küstennahe Grundwasserleiter bilden die Schnittstelle zwischen ozeanischen und terrestrischen hydrologischen Systemen und sind eine wichtige Süßwasserquelle für die Menschen in Küstenregionen. Der Klimawandel führt durch die thermische Ausdehnung des Meerwassers und das Abschmelzen der Gletscher zu einem Anstieg des Meeresspiegels. Dies verlagert die Süßwasser-Salzwasser-Grenzfläche im Untergrund ins Landesinnere, wodurch Salzwassereintritt in Grundwasserleiter gefördert wird. Extremwetterereignisse wie Sturmfluten, Zyklone und Tsunamis – deren Wahrscheinlichkeit infolge des Klimawandels zunimmt – verschärfen die Situation zusätzlich, da sie Salzwasser in den Untergrund drücken können. Besonders gefährdet sind flache Deltas (z. B. das Ganges-Brahmaputra- oder Mekong-Delta), kleine Inselstaaten sowie Regionen mit stark genutzten Küstenaquiferen und hoher Bevölkerungsdichte (z. B. Jakarta, Gaza, Palästina). [1, S.106, 107]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.entstehung.climate.indirect.title",
                "Indirekte Auswirkungen des Klimawandels auf regionale Wasserbilanzen"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.entstehung.climate.indirect.text",
                  "Die Folgen des Klimawandels auf die Grundwasservorkommen können tatsächlich am stärksten über indirekte Effekte auf den Bewässerungswasserbedarf auftreten. Wie bereits erwähnt, treten Extremwetterereignisse häufiger auf, und die klimatischen Schwankungen nehmen zu. Dies bedeutet, dass in vielen Regionen tendenziell mehr Dürren auftreten und sowohl die Bodenfeuchte als auch die Füllstände der Oberflächengewässer abnehmen. Aufgrund der weltweit steigenden Nahrungsmittelnachfrage sind viele Gesellschaften dazu gezwungen, Bewässerungsstrategien zu entwickeln, die eine verstärkte Nutzung und Speicherung von Grundwasser vorsehen. Denn auf die natürliche Bewässerung des Niederschlags wird in Zukunft weniger Verlass sein. [1, S.108]"
                )
              )}
            </p>
          </Card>

          <SourcesSection>
            <h3>
              {t(
                "wasserknappheit.entstehung.literature.title",
                "Literaturverzeichnis"
              )}
            </h3>
            <ol>
              <li id="quelle-1">
                <LinkifyText>
                  {t(
                    "wasserknappheit.entstehung.literature.ref1",
                    "[1] United Nations, Hrsg., The United Nations World Water Development Report 2022: Groundwater: Making the invisible visible. Paris: UNESCO, 2022."
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-2">
                <LinkifyText>
                  {t(
                    "wasserknappheit.entstehung.literature.ref2",
                    '[2] Umweltbundesamt (Hrsg.), "Eutrophierung", Umweltbundesamt, 2024. Zugegriffen: 6. Oktober 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/themen/wasser/gewaesser/meere/nutzung-belastungen/eutrophierung'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
